import type { FirstPartyCatalog } from '@/lib/firstparty/types';

// Raw JSON shape persisted in fp_app_catalog. Media paths have already been
// rewritten to public Storage URLs by the capture pipeline.
export type RawScreen = {
  file?: string;
  id?: number | string;
  screen_name?: string;
  screen_type?: string;
  category?: string | null;
  primary_cta?: string | null;
};

export type RawFlow = {
  kind?: string;
  no?: number;
  name?: string;
  category?: string | null;
  where?: string | null;
  tags?: string[];
  clip?: string | null;
  clip_dur_s?: number | null;
  screens?: RawScreen[];
};

export type RawAnim = {
  file?: string;
  poster?: string | null;
  kind?: string;
  name?: string;
  description?: string;
  dur_s?: number;
  category?: string | null;
  flow?: string | null;
  flow_no?: number | null;
};

export type RawWalkthrough = {
  part?: string;
  speed?: string;
  url?: string;
  duration_s?: number | null;
};

export type RawCatalog = {
  app?: string;
  platform?: string;
  flows?: RawFlow[];
  animations?: RawAnim[];
  full_video?: string | null;
  full_video_meta?: { clean_duration_s?: number };
  walkthroughs?: RawWalkthrough[];
};

const MEDIA_LIBRARY_KIND = 'media-library';

function orderFlows(flows: RawFlow[]): RawFlow[] {
  const mediaLibrary = flows
    .filter((flow) => flow?.kind === MEDIA_LIBRARY_KIND)
    .sort((a, b) => (a.no ?? 0) - (b.no ?? 0));
  return [...flows.filter((flow) => flow?.kind !== MEDIA_LIBRARY_KIND), ...mediaLibrary];
}

const WALKTHROUGH_PARTS: Record<string, string> = {
  loggedin: 'Signed-in',
  guest: 'Guest',
  signup: 'Sign-up',
  signin: 'Sign-in',
  newaccount: 'New account',
  returning: 'Returning user',
};

/** A walkthrough part for people: "loggedin" → "Signed-in walkthrough", "pro-settings" → "Pro settings walkthrough". */
export function walkthroughLabel(part: string): string {
  const words = WALKTHROUGH_PARTS[part] ?? part.replace(/[-_]+/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} walkthrough`;
}

/** Flow clips plus app-level walkthrough parts (1x and 3x of one part count once). */
export function countWalkthroughs(catalog: FirstPartyCatalog): number {
  const clips = catalog.flows.filter((flow) => Boolean(flow.clip_url)).length;
  return clips + new Set((catalog.walkthroughs ?? []).map((walkthrough) => walkthrough.part)).size;
}

/**
 * Convert a persisted capture catalog into the stable UI model shared by app
 * detail pages and the public design library.
 */
export function normalizeFirstPartyCatalog(cat: RawCatalog): FirstPartyCatalog {
  const rawFlows = orderFlows(Array.isArray(cat.flows) ? cat.flows : []);

  return {
    app: cat.app || 'App',
    platform: cat.platform || 'ios',
    flows: rawFlows
      .map((flow, flowIndex) => ({
        name: flow.name || `Flow ${flowIndex + 1}`,
        category: flow.category ?? null,
        where: flow.where ?? null,
        no: flow.no ?? null,
        tags: flow.tags || [],
        clip_url: flow.clip || null,
        clip_dur_s: flow.clip_dur_s ?? null,
        screens: (flow.screens || [])
          .filter((screen) => screen?.file)
          .map((screen, screenIndex) => ({
            id: `fp-${flowIndex}-${screen.id ?? screenIndex}`,
            image_url: screen.file as string,
            screen_name: screen.screen_name,
            screen_type: screen.screen_type,
            category: screen.category ?? null,
            primary_cta: screen.primary_cta ?? null,
          })),
      }))
      .filter((flow) => flow.screens.length > 0),
    animations: (cat.animations || [])
      .filter((animation) => animation?.file)
      .map((animation) => ({
        mp4_url: animation.file as string,
        poster_url: animation.poster ?? null,
        kind: animation.kind,
        name: animation.name,
        description: animation.description,
        dur_s: animation.dur_s,
        category: animation.category ?? null,
        flow: animation.flow ?? null,
        flow_no: animation.flow_no ?? null,
      })),
    full_video: cat.full_video || null,
    full_video_duration_s: (cat.full_video_meta || {}).clean_duration_s ?? null,
    walkthroughs: (Array.isArray(cat.walkthroughs) ? cat.walkthroughs : [])
      .filter((walkthrough) => walkthrough?.url)
      .map((walkthrough) => ({
        part: walkthrough.part || 'walkthrough',
        speed: walkthrough.speed || '1x',
        url: walkthrough.url as string,
        duration_s: walkthrough.duration_s ?? null,
      })),
  };
}
